import type { components } from '../../api/schema'

export type DefinitionSpec = components['schemas']['ThematicDefinitionSpec']
export type ClassSpec = components['schemas']['ThematicClassSpec']
export type Condition = components['schemas']['ThematicCondition']
export type Definition = components['schemas']['ThematicDefinition']
export type DefinitionSource = components['schemas']['ThematicSource']

export type ConditionType = NonNullable<Condition['type']>
/** What a source yields, which decides the conditions a class may use. */
export type ValueKind = 'enum' | 'boolean' | 'number' | 'date' | 'text' | 'asset_type' | 'grouper'

export const TARGETS = ['lot', 'room', 'equipment', 'building'] as const
export type Target = (typeof TARGETS)[number]
export const MAX_CLASSES = 20
export const VIEWS = ['stacking', 'floor', 'site'] as const
export type View = (typeof VIEWS)[number]
export const SOURCE_KINDS = ['property', 'asset_type', 'grouper', 'start_date', 'commercial_status'] as const
/** Property types a plan can be coloured by. */
export const ELIGIBLE_PROPERTY_TYPES = ['enum', 'boolean', 'number', 'date', 'text'] as const

/** The value kind of a source, from the type of its property. */
export function valueKindOf(sourceKind: string, propertyType?: string): ValueKind | null {
  switch (sourceKind) {
    case 'asset_type': return 'asset_type'
    case 'grouper': return 'grouper'
    case 'start_date': return 'date'
    // The six commercial statuses of a lettable object (spec 2026-10-25-06).
    case 'commercial_status': return 'enum'
    case 'property':
      return (ELIGIBLE_PROPERTY_TYPES as readonly string[]).includes(propertyType ?? '') ? (propertyType as ValueKind) : null
    default: return null
  }
}

/** The condition types that fit a value kind. The builder offers nothing else (the server enforces it too). */
export function conditionTypesFor(kind: ValueKind | null): ConditionType[] {
  switch (kind) {
    case 'enum':
    case 'boolean':
    case 'asset_type':
    case 'grouper':
      return ['in', 'is_set', 'is_unset']
    case 'number': return ['range', 'is_set', 'is_unset']
    case 'date': return ['date_range', 'is_set', 'is_unset']
    case 'text': return ['text', 'is_set', 'is_unset']
    default: return []
  }
}

/** A bare definition (no classes) is one class per value: only for these kinds. */
export function canBeBare(kind: ValueKind | null): boolean {
  return kind === 'enum' || kind === 'boolean' || kind === 'asset_type' || kind === 'grouper'
}

export function blankCondition(type: ConditionType): Condition {
  switch (type) {
    case 'in': return { type, values: [] }
    case 'range': return { type }
    case 'date_range': return { type, from: 'P0D', to: 'P6M' }
    case 'text': return { type, op: 'contains', value: '' }
    default: return { type }
  }
}

/** The views a definition's targets are drawn on. */
export function viewsFor(targets: readonly string[]): View[] {
  const out: View[] = []
  if (targets.includes('lot')) out.push('stacking')
  if (targets.includes('room') || targets.includes('equipment')) out.push('floor')
  if (targets.includes('building')) out.push('site')
  return out
}

const PALETTE = ['#4A8CF7', '#E8933A', '#2ECC91', '#9B72F7', '#F05252', '#F0B429', '#14B8A6', '#EC4899', '#84CC16', '#06B6D4', '#A16207', '#64748B']

/** The next palette colour no class uses yet. */
export function nextColour(classes: readonly ClassSpec[]): string {
  const used = new Set(classes.map((c) => c.color.toUpperCase()))
  return PALETTE.find((c) => !used.has(c)) ?? PALETTE[classes.length % PALETTE.length]
}

/** A class code no other class uses (`class_1`, `class_2`...). */
export function nextClassCode(classes: readonly ClassSpec[]): string {
  const used = new Set(classes.map((c) => c.code))
  let i = classes.length + 1
  while (used.has(`class_${i}`)) i++
  return `class_${i}`
}

export function blankClass(classes: readonly ClassSpec[], kind: ValueKind | null): ClassSpec {
  const type = conditionTypesFor(kind)[0] ?? 'is_set'
  return {
    code: nextClassCode(classes),
    label: { en: '', fr: '' },
    color: nextColour(classes),
    condition: blankCondition(type),
  }
}

/** Moves one class, as a drag does. */
export function moveClass<T>(items: readonly T[], from: number, to: number): T[] {
  if (from === to || from < 0 || to < 0 || from >= items.length || to >= items.length) return [...items]
  const next = [...items]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}

/** A code from a name: lowercase, ASCII, underscores. */
export function slugify(name: string): string {
  const base = name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
  return /^[a-z]/.test(base) ? base : base ? `t_${base}` : ''
}

export function blankDefinition(): DefinitionSpec {
  return {
    code: '',
    name: { en: '', fr: '' },
    targets: ['lot'],
    source: { kind: 'property' },
    via_grouper: false,
    classes: [],
    unset_class: false,
    other_class: false,
    suggested_in: {},
  }
}

/** Number of classes the legend will show: the explicit ones plus the two implicit ones that are on. */
export function classCount(def: Pick<DefinitionSpec, 'classes' | 'other_class' | 'unset_class'>): number {
  return (def.classes?.length ?? 0) + (def.other_class ? 1 : 0) + (def.unset_class ? 1 : 0)
}

const DURATION = /^-?P(?:\d+Y)?(?:\d+M)?(?:\d+W)?(?:\d+D)?$/

/** True for an ISO 8601 duration (days, weeks, months, years) or a YYYY-MM-DD date, or empty (open). */
export function isDateBound(v: string): boolean {
  if (v === '') return true
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return true
  return v !== 'P' && v !== '-P' && DURATION.test(v)
}
